import axios from "axios";
import { Note } from "../types/note";

// ================= Base Axios =================

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export const api = axios.create({
  baseURL: new URL("/api/v1", API_URL).toString(),
  withCredentials: true,
  headers: { Accept: "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("jwt");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ================= Types =================

export interface AdminStats {
  // existants
  user_count: number;
  diagnostic_count: number;
  average_score: number;

  // complétions (gardées)
  completion_rate_today: number;  // 0..1
  completion_rate_7d: number;     // 0..1
  completion_rate_30d: number;    // 0..1
}

// ================= Auth =================

export async function loginWithGoogle(credential: string) {
  const response = await api.post("/auth/google", { credential });
  return response.data;
}

// ================= Profile =================

export async function fetchUserProfile() {
  const response = await api.get("/profile");
  return response.data;
}

export async function fetchUserProfileWithAuth() {
  const response = await api.get("/profile");
  return response.data;
}

export type CurrentUser = {
  id: number;
  name?: string | null;
  email: string;
  image?: string | null;
  time_zone: string;
  current_streak?: number;
  best_streak?: number;
  total_streak_days?: number;
  last_streak_date?: string | null;
};

export async function getCurrentUser(): Promise<CurrentUser> {
  const res = await api.get("/users/me");
  return res.data as CurrentUser;
}

/** 🔥 NEW: mettre à jour le time_zone (IANA) */
export async function updateCurrentUserTZ(time_zone: string): Promise<CurrentUser> {
  const res = await api.patch("/users/me", { user: { time_zone } });
  return res.data as CurrentUser;
}

// ================= Questions & Diagnostics =================

export async function fetchQuestions(version = "v1") {
  const response = await api.get(`/questions?version=${version}`);
  return response.data.questions;
}

export async function submitDiagnostic(payload: any) {
  const response = await api.post("/diagnostics", payload);
  return response.data;
}

export async function fetchDiagnosticById(id: string) {
  const response = await api.get(`/diagnostics/${id}`);
  return response.data;
}

export async function fetchDiagnosticWithAnswers(id: number) {
  const response = await api.get(`/diagnostics/${id}`);
  return response.data;
}

export async function fetchDiagnosticsByUserId(userId: number) {
  const response = await api.get(`/diagnostics/user/${userId}`);
  return response.data;
}

export async function fetchMyDiagnostics() {
  const response = await api.get("/diagnostics");
  return response.data;
}

export async function fetchUserDiagnostics() {
  const response = await api.get("/diagnostics");
  return response.data.diagnostics || [];
}

// ================= Admin =================

export async function fetchAdminStats(): Promise<AdminStats> {
  const response = await api.get("/admin/stats");
  return response.data as AdminStats;
}

// ================= Journal Entries =================

export async function fetchJournalEntries() {
  const response = await api.get("/journal_entries");
  return response.data;
}

export async function createJournalEntry(entry: {
  category: string;
  content: string;
  action?: string | null;
}) {
  const response = await api.post("/journal_entries", {
    journal_entry: entry,
  });
  return response.data;
}

export const deleteJournalEntry = async (id: number) => {
  const response = await api.delete(`/journal_entries/${id}`);
  return response.status === 204 || response.status === 200;
};

// Users
export async function deleteUser(id: number) {
  const response = await api.delete(`/users/${id}`);
  return response.data;
}

// ================= Whitelist =================

export async function fetchAuthorizedEmails() {
  const response = await api.get("/authorized_emails");
  return response.data;
}

export async function addAuthorizedEmail(email: string) {
  const response = await api.post("/authorized_emails", { email });
  return response.data;
}

export async function deleteAuthorizedEmail(id: number) {
  const response = await api.delete(`/authorized_emails/${id}`);
  return response.status === 204;
}

export async function fetchQuestionCount(version = "v1") {
  const response = await api.get(`/questions?version=${version}`);
  return response.data.questions?.length || 0;
}

// ================= Routines =================

export async function fetchRoutines() {
  const response = await api.get("/routines");
  return response.data;
}

export async function createRoutine(routine: {
  activity: string;
  duration: string;
  period: "morning" | "evening";
  date: string;
}) {
  const response = await api.post("/routines", { routine });
  return response.data;
}

export async function updateRoutine(
  id: number,
  data: Partial<{
    activity: string;
    duration: string;
    done: boolean;
    timestamp: string | null;
  }>
) {
  const response = await api.put(`/routines/${id}`, { routine: data });
  return response.data;
}

export async function deleteRoutine(id: number) {
  const response = await api.delete(`/routines/${id}`);
  return response.status === 204;
}

export async function fetchMissedRoutines(date: string) {
  const response = await api.get(`/routines/missed?date=${date}`);
  return response.data;
}

// ================= Notes =================

export async function fetchNotes(context?: string): Promise<Note[]> {
  const res = await api.get("/notes", { params: context ? { context } : {} });
  return res.data;
}

export async function fetchNote(id: number): Promise<Note> {
  const res = await api.get(`/notes/${id}`);
  return res.data;
}

export async function createNote(note: { title: string; content: string; context?: string }): Promise<Note> {
  const res = await api.post("/notes", { note });
  return res.data;
}

export async function updateNote(
  id: number,
  note: Partial<Pick<Note, "title" | "content" | "pinned">>
): Promise<Note> {
  const res = await api.put(`/notes/${id}`, { note });
  return res.data;
}

export async function deleteNote(id: number): Promise<void> {
  await api.delete(`/notes/${id}`);
}

// ================= Streak & Classements =================

export async function fetchUserStreak(): Promise<{
  current_streak: number;
  best_streak: number;
  total_days: number;
  level: string;
  last_validated_date?: string;
}> {
  const response = await api.get("/users/streak");
  return response.data;
}

export async function fetchUserRankings(): Promise<
  {
    id: number;
    name: string;
    email: string;
    total_streak_days: number;
    current_streak: number;
    best_streak: number;
    level: string;
  }[]
> {
  const response = await api.get("/admin/rankings");
  return response.data;
}

// ================= Chat =================

export async function fetchMessagesAPI() {
  const response = await api.get("/chat_messages");
  return response.data;
}

export async function sendMessageAPI(message: string) {
  const response = await api.post("/chat_messages", { message });
  return response.data;
}

export async function uploadDocumentAPI(file: File) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post("/chat_documents", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response.data;
}

export async function clearChatThreadAPI() {
  const response = await api.delete("/chat_messages/thread");
  return response.data;
}

// ================= Challenges (public) =================

export type ChallengeDTO = {
  id: number;
  title: string;
  description?: string;
  kind: "daily" | "weekly";
  challenge_date?: string | null;
  iso_year?: number | null;
  iso_week?: number | null;
  published: boolean;
  done?: boolean; // pour /active
};

export async function fetchActiveChallenges(): Promise<{
  daily: ChallengeDTO[];
  weekly: ChallengeDTO[];
}> {
  const res = await api.get("/challenges/active");
  return res.data;
}

export async function toggleChallengeValidation(
  id: number,
  done: boolean,
  date?: string // ISO "YYYY-MM-DD"
): Promise<{ done: boolean }> {
  const body: any = { done };
  if (date) body.date = date;
  const res = await api.post(`/challenges/${id}/validate`, body);
  return res.data;
}

// ================= Leaderboard (public) =================

export async function fetchLeaderboard(
  limit = 10
): Promise<
  { id: number; name: string; email: string; score: number; current_streak: number; best_streak: number; level: string }[]
> {
  const res = await api.get(`/leaderboard?limit=${limit}`);
  return res.data;
}

// ================= Admin Challenges =================

export async function adminListChallenges(): Promise<ChallengeDTO[]> {
  const res = await api.get("/admin/challenges");
  return res.data;
}

export async function adminCreateChallenge(payload: {
  title: string;
  description?: string;
  kind: "daily" | "weekly";
  challenge_date?: string | null; // ISO date "YYYY-MM-DD" si daily
  iso_year?: number | null;       // si weekly
  iso_week?: number | null;       // si weekly
  published?: boolean;
}): Promise<ChallengeDTO> {
  const res = await api.post("/admin/challenges", { challenge: payload });
  return res.data;
}

export async function adminUpdateChallenge(
  id: number,
  payload: Partial<{
    title: string;
    description?: string;
    kind: "daily" | "weekly";
    challenge_date?: string | null;
    iso_year?: number | null;
    iso_week?: number | null;
    published?: boolean;
  }>
): Promise<ChallengeDTO> {
  const res = await api.patch(`/admin/challenges/${id}`, { challenge: payload });
  return res.data;
}

export async function adminDeleteChallenge(id: number): Promise<void> {
  await api.delete(`/admin/challenges/${id}`);
}

export async function adminResetLeaderboard(): Promise<void> {
  await api.post("/admin/challenges/reset_leaderboard");
}

// ================= Documents (PDF) =================

export type ResourceDocumentDTO = {
  id: number;
  title: string;
  description?: string;
  created_at: string;
  uploader?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  download_url?: string | null;
};

// Admin
export async function adminListDocuments(): Promise<ResourceDocumentDTO[]> {
  const res = await api.get("/admin/resource_documents");
  return res.data;
}

export async function adminUploadDocument(payload: {
  title: string;
  description?: string;
  file: File;
}): Promise<ResourceDocumentDTO> {
  const form = new FormData();
  form.append("title", payload.title);
  if (payload.description) form.append("description", payload.description);
  form.append("file", payload.file);
  const res = await api.post("/admin/resource_documents", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function adminDeleteDocument(id: number): Promise<void> {
  await api.delete(`/admin/resource_documents/${id}`);
}

// Users
export async function listDocuments(): Promise<ResourceDocumentDTO[]> {
  const res = await api.get("/resource_documents");
  return res.data;
}

// ================= Exercices =================

export type ExerciseDTO = {
  id: number;
  name: string;
  description?: string | null;
  camera_angle: "profile" | "face";
  min_reps: number;
  instructions?: string | null;
  exercise_key?: string | null;
  published: boolean;
  has_coach_video: boolean;
  coach_video_url?: string | null;
  created_at: string;
};

export type ScoreBand = "Excellent" | "Bien" | "À affiner" | "À travailler" | "Fondamentaux";

export type ExerciseAnalysisDTO = {
  id: number;
  exercise_id: number;
  status: "pending" | "processing" | "done" | "error";
  feedback?: string | null;
  coach_comment?: string | null;
  coach_comment_link?: string | null;
  error_message?: string | null;
  overall_score?: number | null;
  score_band?: ScoreBand | null;
  exercise_label?: string | null;
  rep_count?: number | null;
  exercise_name?: string | null;
  created_at: string;
  updated_at: string;
};

export async function fetchPendingAnalyses(): Promise<ExerciseAnalysisDTO[]> {
  const res = await api.get("/exercise_analyses/pending");
  return Array.isArray(res.data) ? res.data : [];
}

export async function retryExerciseAnalysis(id: number): Promise<ExerciseAnalysisDTO> {
  const res = await api.post(`/exercise_analyses/${id}/retry_analysis`);
  return res.data;
}

export async function fetchExercises(): Promise<ExerciseDTO[]> {
  const res = await api.get("/exercises");
  return Array.isArray(res.data) ? res.data : [];
}

export async function fetchExercise(id: number): Promise<ExerciseDTO> {
  const res = await api.get(`/exercises/${id}`);
  return res.data;
}

export async function submitExerciseVideo(
  exerciseId: number,
  videoFile: File,
  selectedAngle?: string
): Promise<ExerciseAnalysisDTO> {
  const form = new FormData();
  form.append("exercise_id", String(exerciseId));
  form.append("user_video", videoFile);
  if (selectedAngle) form.append("selected_angle", selectedAngle);
  const res = await api.post("/exercise_analyses", form);
  return res.data;
}

export async function pollExerciseAnalysis(id: number): Promise<ExerciseAnalysisDTO> {
  const res = await api.get(`/exercise_analyses/${id}`);
  return res.data;
}

export async function fetchExerciseHistory(exerciseId: number): Promise<ExerciseAnalysisDTO[]> {
  const res = await api.get(`/exercises/${exerciseId}/analyses`);
  return res.data;
}

// Admin — Analyses
export interface AdminAnalysisDTO {
  id: number;
  status: string;
  error_message: string | null;
  user_name: string;
  user_email: string;
  exercise_name: string;
  exercise_label: string | null;
  overall_score: number | null;
  scoring_method: string | null;
  feedback: string | null;
  vision_summary: string | null;
  errors: { key: string; description: string; severity: string; cue: string }[];
  strengths: string[];
  has_video: boolean;
  video_url: string | null;
  coach_comment: string | null;
  coach_comment_link: string | null;
  ai_error: string | null;
  reviewed: boolean;
  created_at: string;
}

export async function adminListAnalyses(): Promise<AdminAnalysisDTO[]> {
  const res = await api.get("/admin/exercise_analyses");
  return res.data;
}

export async function adminDeleteAnalysisVideo(id: number): Promise<void> {
  await api.delete(`/admin/exercise_analyses/${id}/destroy_video`);
}

export async function adminDeleteAnalysis(id: number): Promise<void> {
  await api.delete(`/admin/exercise_analyses/${id}`);
}

export async function adminReanalyze(id: number): Promise<void> {
  await api.post(`/admin/exercise_analyses/${id}/reanalyze`);
}

export async function adminUpdateAiError(id: number, ai_error: string): Promise<void> {
  await api.patch(`/admin/exercise_analyses/${id}/update_ai_error`, { ai_error });
}

export async function adminToggleReviewed(id: number): Promise<{ reviewed: boolean; video_purged: boolean }> {
  const res = await api.patch(`/admin/exercise_analyses/${id}/toggle_reviewed`);
  return res.data;
}

export async function adminUpdateCoachComment(id: number, comment: string, link: string): Promise<void> {
  await api.patch(`/admin/exercise_analyses/${id}/update_comment`, {
    coach_comment: comment,
    coach_comment_link: link,
  });
}

// Admin
export async function adminListExercises(): Promise<ExerciseDTO[]> {
  const res = await api.get("/admin/exercises");
  return res.data;
}

export async function adminCreateExercise(payload: {
  name: string;
  description?: string;
  camera_angle: "profile" | "face";
  min_reps: number;
  instructions?: string;
  exercise_key?: string;
  published: boolean;
  coach_video?: File;
}): Promise<ExerciseDTO> {
  const form = new FormData();
  form.append("name", payload.name);
  if (payload.description) form.append("description", payload.description);
  form.append("camera_angle", payload.camera_angle);
  form.append("min_reps", String(payload.min_reps));
  if (payload.instructions) form.append("instructions", payload.instructions);
  if (payload.exercise_key) form.append("exercise_key", payload.exercise_key);
  form.append("published", String(payload.published));
  if (payload.coach_video) form.append("coach_video", payload.coach_video);
  const res = await api.post("/admin/exercises", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function adminUpdateExercise(
  id: number,
  payload: Partial<{
    name: string;
    description: string;
    camera_angle: "profile" | "face";
    min_reps: number;
    instructions: string;
    exercise_key: string;
    published: boolean;
    coach_video: File;
  }>
): Promise<ExerciseDTO> {
  const form = new FormData();
  if (payload.name !== undefined) form.append("name", payload.name);
  if (payload.description !== undefined) form.append("description", payload.description);
  if (payload.camera_angle !== undefined) form.append("camera_angle", payload.camera_angle);
  if (payload.min_reps !== undefined) form.append("min_reps", String(payload.min_reps));
  if (payload.instructions !== undefined) form.append("instructions", payload.instructions);
  if (payload.exercise_key !== undefined) form.append("exercise_key", payload.exercise_key);
  if (payload.published !== undefined) form.append("published", String(payload.published));
  if (payload.coach_video) form.append("coach_video", payload.coach_video);
  const res = await api.patch(`/admin/exercises/${id}`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function adminDeleteExercise(id: number): Promise<void> {
  await api.delete(`/admin/exercises/${id}`);
}

// ================= Measurements =================

export type MeasurementDTO = {
  id: number;
  measured_at: string;   // ISO date "YYYY-MM-DD" ou ISO datetime (on renvoie date ISO dans l'API)
  waist_cm?: number | null;
  hip_cm?: number | null;
  chest_cm?: number | null;
  thigh_cm?: number | null;
  weight_kg?: number | null;
  created_at?: string;
  updated_at?: string;
};

export async function fetchMeasurements(): Promise<MeasurementDTO[]> {
  const res = await api.get("/measurements");
  return res.data.measurements ?? [];
}

export async function createMeasurement(payload: Partial<MeasurementDTO>): Promise<MeasurementDTO> {
  const res = await api.post("/measurements", { measurement: payload });
  return res.data;
}

export async function updateMeasurement(id: number, payload: Partial<MeasurementDTO>): Promise<MeasurementDTO> {
  const res = await api.put(`/measurements/${id}`, { measurement: payload });
  return res.data;
}

export async function deleteMeasurement(id: number): Promise<void> {
  await api.delete(`/measurements/${id}`);
}

export async function fetchUserMeasurements(userId: number): Promise<MeasurementDTO[]> {
  const res = await api.get(`/users/${userId}/measurements`);
  return res.data.measurements ?? [];
}

// ================= Calendar =================

export type CalendarEventDTO = {
  id: string;
  summary: string;
  start: string;      // ISO
  end: string;        // ISO
  description?: string | null;
  location?: string | null;
  color?: string | null;
  time_zone?: string | null; // ✅ ajouté
};

export async function fetchEvents(date?: string): Promise<CalendarEventDTO[]> {
  const res = await api.get("/calendar/events", { params: { date } });
  return res.data.events ?? [];
}

export async function createCalendarEvent(
  summary: string,
  start: string,
  end: string,
  description?: string,
  location?: string,
  color?: string,
  time_zone?: string // ✅ ajouté
): Promise<CalendarEventDTO> {
  const res = await api.post("/calendar/events", {
    summary,
    start,
    end,
    description,
    location,
    color,
    time_zone, // ✅ envoyé au backend
  });
  return res.data;
}

export async function updateCalendarEvent(
  id: string,
  updates: Partial<CalendarEventDTO> & { time_zone?: string } // ✅ accepte aussi time_zone
): Promise<CalendarEventDTO> {
  const res = await api.patch(`/calendar/events/${id}`, updates);
  return res.data;
}

export async function deleteCalendarEvent(id: string): Promise<void> {
  await api.delete(`/calendar/events/${id}`);
}


